import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../services/api';
import ServerCard from '../components/ServerCard';
import Modal from '../components/Modal';
import { User, Shield, Calendar, ChevronRight, Users, Clock, LayoutGrid, AppWindow, Globe, Sun, Moon } from 'lucide-react';
import '../styles/SharedStyles.css';

const AdminDashboard = () => {
  const { t } = useLanguage();
  const { isAdmin, user: currentUser } = useAuth();
  const { settings, updateSettings } = useSettings();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('pending'); // pending, active, servers, widgets, settings

  const [users, setUsers] = useState([]);
  const [servers, setServers] = useState([]);
  const [widgets, setWidgets] = useState([]);

  // Modal states
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const [isServerModalOpen, setIsServerModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [serverForm, setServerForm] = useState({ name: '', connectionType: 'ip_port', ip: '', port: '', url: '', status: 'online', cpu: '0%', ram: '0 GB' });

  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', password: '', role: 'user' });

  const [isWidgetModalOpen, setIsWidgetModalOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [widgetForm, setWidgetForm] = useState({ type: 'app', title: '', url: '', description: '' });

  const refreshData = useCallback(async () => {
    try {
      const [usersData, serversData, widgetsData] = await Promise.all([
        api.users.getAll(),
        api.servers.getAll(),
        api.widgets.getAll()
      ]);
      setUsers(usersData);
      setServers(serversData);
      setWidgets(widgetsData);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      refreshData();
    }
  }, [isAdmin, refreshData]);

  if (!isAdmin) {
    return <div className="container text-center mt-8 text-danger">Access Denied</div>;
  }

  const pendingUsers = users.filter(u => u.status === 'pending');
  const activeUsers = users.filter(u => u.status === 'active');

  const handleApprove = async (id) => {
    await api.users.update(id, { status: 'active' });
    refreshData();
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm(t('confirmDeleteUser'))) {
      await api.users.delete(id);
      refreshData();
    }
  };

  const handleRoleToggle = async (user) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    await api.users.update(user.id, { role: newRole });
    refreshData();
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setIsPasswordModalOpen(true);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (selectedUser && newPassword) {
      await api.users.update(selectedUser.id, { password: newPassword });
      setIsPasswordModalOpen(false);
      refreshData();
    }
  };

  const openServerModal = (server = null) => {
    if (server) {
      setEditingServer(server);
      setServerForm(server);
    } else {
      setEditingServer(null);
      setServerForm({ name: '', connectionType: 'ip_port', ip: '', port: '', url: '', status: 'online', cpu: '0%', ram: '0 GB' });
    }
    setIsServerModalOpen(true);
  };

  const handleServerSubmit = async (e) => {
    e.preventDefault();
    if (editingServer) {
      await api.servers.update(editingServer.id, serverForm);
    } else {
      await api.servers.add(serverForm);
    }
    setIsServerModalOpen(false);
    refreshData();
  };

  const handleServerDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this server?')) {
      await api.servers.delete(id);
      refreshData();
    }
  };

  const openWidgetModal = (widget = null) => {
    if (widget) {
      setEditingWidget(widget);
      setWidgetForm(widget);
    } else {
      setEditingWidget(null);
      setWidgetForm({ type: 'app', title: '', url: '', description: '' });
    }
    setIsWidgetModalOpen(true);
  };

  const handleWidgetSubmit = async (e) => {
    e.preventDefault();
    if (editingWidget) {
      await api.widgets.update(editingWidget.id, widgetForm);
    } else {
      await api.widgets.add(widgetForm);
    }
    setIsWidgetModalOpen(false);
    refreshData();
  };

  const handleWidgetDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this widget?')) {
      await api.widgets.delete(id);
      refreshData();
    }
  };

  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    await api.users.create({ ...userForm, status: 'active' });
    setIsAddUserModalOpen(false);
    setUserForm({ email: '', password: '', role: 'user' });
    refreshData();
  };

  return (
    <div className={`shared-page ${theme}`}>
      <div className="glow-effect glow-1"></div>
      <div className="glow-effect glow-2"></div>
      <div className="container shared-content" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
      <div className="shared-section-header">
        <h2>{t('adminDashboard')}</h2>
        <p>{t('usersPageSubtitle')}</p>
      </div>

      <div className="flex gap-4 mb-4" style={{ borderBottom: '1px solid var(--bg-card-hover)', paddingBottom: '1rem' }}>
        <button
          className={`btn ${activeTab === 'pending' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('pending')}
        >
          {t('pendingUsers')} ({pendingUsers.length})
        </button>
        <button
          className={`btn ${activeTab === 'active' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('active')}
        >
          {t('activeUsers')}
        </button>
        <button
          className={`btn ${activeTab === 'servers' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('servers')}
        >
          {t('serverManagement')}
        </button>
        <button
          className={`btn ${activeTab === 'widgets' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('widgets')}
        >
          {t('widgetsAndApps')}
        </button>
        <button
          className={`btn ${activeTab === 'settings' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('settings')}
        >
          Customization
        </button>
      </div>

      {activeTab === 'pending' && (
        <div>
          {pendingUsers.length === 0 ? (
            <div className="shared-empty">
              <Users size={48} />
              <p>{t('noPendingUsers')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingUsers.map(u => (
                <div key={u.id} className="server-feature-card" style={{ '--server-color': '#f59e0b' }}>
                  <div className="server-card-icon" style={{ backgroundColor: '#f59e0b' }}>
                    <User size={28} />
                  </div>
                  <h3>{u.email}</h3>
                  <div className="server-card-status">
                    <Clock size={14} />
                    <span>Pending Approval</span>
                  </div>
                  <div className="server-card-specs">
                    <div className="spec-item">
                      <Calendar size={16} />
                      <span>{new Date(u.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="server-card-actions">
                    <button className="server-btn-edit" onClick={() => handleApprove(u.id)}>
                      {t('approve')}
                    </button>
                    <button className="server-btn-delete" onClick={() => handleDeleteUser(u.id)}>
                      {t('deleteUser')}
                    </button>
                  </div>
                  <div className="server-card-arrow">
                    <ChevronRight size={20} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'active' && (
        <div>
          <div className="flex justify-end mb-4">
            <button className="btn btn-primary" onClick={() => setIsAddUserModalOpen(true)}>{t('addUser')}</button>
          </div>
          {activeUsers.length === 0 ? (
            <div className="shared-empty">
              <Users size={48} />
              <p>{t('noActiveUsers')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeUsers.map(u => {
                const isAdmin = u.role === 'admin';
                const iconColor = isAdmin ? '#8b5cf6' : '#10b981';
                return (
                  <div key={u.id} className="server-feature-card" style={{ '--server-color': iconColor }}>
                    <div className="server-card-icon" style={{ backgroundColor: iconColor }}>
                      {isAdmin ? <Shield size={28} /> : <User size={28} />}
                    </div>
                    <h3>{u.email}</h3>
                    <div className="server-card-status">
                      {isAdmin ? <Shield size={14} /> : <User size={14} />}
                      <span>{isAdmin ? t('administrator') : t('user')}</span>
                    </div>
                    <div className="server-card-specs">
                      <div className="spec-item">
                        <Calendar size={16} />
                        <span>{new Date(u.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="server-card-actions">
                      <button className="server-btn-edit" onClick={() => openPasswordModal(u)}>
                        {t('changePassword')}
                      </button>
                      {currentUser?.id !== u.id && (
                        <button className="server-btn-delete" onClick={() => handleDeleteUser(u.id)}>
                          {t('deleteUser')}
                        </button>
                      )}
                    </div>
                    <div className="server-card-arrow">
                      <ChevronRight size={20} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'servers' && (
        <div>
          <div className="flex justify-end mb-4">
            <button className="btn btn-primary" onClick={() => openServerModal()}>{t('addServer')}</button>
          </div>
          {servers.length === 0 ? (
            <div className="server-card text-center">{t('noServers')}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {servers.map(server => (
                <ServerCard
                  key={server.id}
                  server={server}
                  isAdmin={true}
                  onEdit={openServerModal}
                  onDelete={handleServerDelete}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'widgets' && (
        <div>
          <div className="flex justify-end mb-4">
            <button className="btn btn-primary" onClick={() => openWidgetModal()}>{t('addWidget')}</button>
          </div>
          {widgets.length === 0 ? (
            <div className="shared-empty">
              <LayoutGrid size={48} />
              <p>{t('noWidgets')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {widgets.map(widget => {
                const isApp = widget.type === 'app';
                const iconColor = isApp ? '#0ea5e9' : '#8b5cf6';
                return (
                  <div key={widget.id} className="server-feature-card" style={{ '--server-color': iconColor }}>
                    <div className="server-card-icon" style={{ backgroundColor: iconColor }}>
                      {isApp ? <AppWindow size={28} /> : <LayoutGrid size={28} />}
                    </div>
                    <h3>{widget.title}</h3>
                    <div className="server-card-status">
                      {isApp ? <AppWindow size={14} /> : <LayoutGrid size={14} />}
                      <span>{t(widget.type)}</span>
                    </div>
                    <div className="server-card-specs">
                      <div className="spec-item">
                        <Globe size={16} />
                        <span style={{ wordBreak: 'break-all' }}>{widget.url || 'No URL'}</span>
                      </div>
                    </div>
                    {widget.description && (
                      <div className="server-card-connection">
                        {widget.description}
                      </div>
                    )}
                    <div className="server-card-actions">
                      <button className="server-btn-edit" onClick={() => openWidgetModal(widget)}>
                        {t('edit')}
                      </button>
                      <button className="server-btn-delete" onClick={() => handleWidgetDelete(widget.id)}>
                        {t('delete')}
                      </button>
                    </div>
                    <div className="server-card-arrow">
                      <ChevronRight size={20} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'settings' && (
        <div>
          <h3 className="mb-4">Customize Dashboard</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Background Settings */}
            <div className="server-card">
              <h4 className="mb-3">Background</h4>
              <div className="input-group mb-3">
                <label>Background Color</label>
                <input
                  type="color"
                  className="input-control"
                  value={settings.backgroundColor}
                  onChange={e => updateSettings({ backgroundColor: e.target.value })}
                />
              </div>
              <div className="input-group mb-3">
                <label>Background Image URL (optional)</label>
                <input
                  type="url"
                  className="input-control"
                  placeholder="https://example.com/image.jpg"
                  value={settings.backgroundImage}
                  onChange={e => updateSettings({ backgroundImage: e.target.value })}
                />
              </div>
              <div className="input-group mb-3">
                <label>Background Opacity: {Math.round(settings.backgroundOpacity * 100)}%</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  className="w-full"
                  value={settings.backgroundOpacity}
                  onChange={e => updateSettings({ backgroundOpacity: parseFloat(e.target.value) })}
                />
              </div>
            </div>

            {/* Icon Settings */}
            <div className="server-card">
              <h4 className="mb-3">Icons & Display</h4>
              <div className="input-group mb-3">
                <label>Icon Size</label>
                <select
                  className="input-control"
                  value={settings.iconSize}
                  onChange={e => updateSettings({ iconSize: e.target.value })}
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
              <div className="input-group mb-3">
                <label>Icon Opacity: {Math.round(settings.iconOpacity * 100)}%</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  className="w-full"
                  value={settings.iconOpacity}
                  onChange={e => updateSettings({ iconOpacity: parseFloat(e.target.value) })}
                />
              </div>
              <div className="input-group">
                <label>Widget Display Mode</label>
                <select
                  className="input-control"
                  value={settings.widgetDisplay}
                  onChange={e => updateSettings({ widgetDisplay: e.target.value })}
                >
                  <option value="normal">Normal</option>
                  <option value="fullsize">Full Size</option>
                  <option value="compact">Compact</option>
                </select>
              </div>
            </div>

            {/* Layout Settings */}
            <div className="server-card">
              <h4 className="mb-3">Layout</h4>
              <div className="input-group mb-3">
                <label>Layout Mode</label>
                <select
                  className="input-control"
                  value={settings.layoutMode}
                  onChange={e => updateSettings({ layoutMode: e.target.value })}
                >
                  <option value="compact">Compact</option>
                  <option value="standard">Standard</option>
                  <option value="spacious">Spacious</option>
                </select>
              </div>
              <div className="input-group mb-3">
                <label>Card Border Radius</label>
                <select
                  className="input-control"
                  value={settings.cardBorderRadius}
                  onChange={e => updateSettings({ cardBorderRadius: e.target.value })}
                >
                  <option value="subtle">Subtle</option>
                  <option value="default">Default</option>
                  <option value="rounded">Rounded</option>
                </select>
              </div>
              <div className="input-group mb-3">
                <label>Font Size</label>
                <select
                  className="input-control"
                  value={settings.fontSize}
                  onChange={e => updateSettings({ fontSize: e.target.value })}
                >
                  <option value="small">Small</option>
                  <option value="normal">Normal</option>
                  <option value="large">Large</option>
                </select>
              </div>
            </div>

            {/* Animation Settings */}
            <div className="server-card">
              <h4 className="mb-3">Effects</h4>
              <div className="input-group" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <label style={{ flex: 1, margin: 0 }}>Enable Animations</label>
                <button
                  className={`btn ${settings.animationsEnabled ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => updateSettings({ animationsEnabled: !settings.animationsEnabled })}
                  style={{ minWidth: '80px' }}
                >
                  {settings.animationsEnabled ? 'On' : 'Off'}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <p style={{ marginBottom: 0, color: 'var(--text-secondary)' }}>
              ✓ Settings are automatically applied and saved to your browser. All changes are persisted across sessions.
            </p>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      <Modal isOpen={isAddUserModalOpen} onClose={() => setIsAddUserModalOpen(false)} title={t('addUser')}>
        <form onSubmit={handleAddUserSubmit}>
          <div className="mb-4">
            <div className="input-group">
              <label>{t('email')}</label>
              <input
                type="email"
                className="input-control"
                value={userForm.email}
                onChange={e => setUserForm({ ...userForm, email: e.target.value })}
                required
              />
            </div>
            <div className="input-group">
              <label>{t('password')}</label>
              <input
                type="password"
                className="input-control"
                value={userForm.password}
                onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                required
              />
            </div>
            <div className="input-group">
              <label>{t('role')}</label>
              <select
                className="input-control"
                value={userForm.role}
                onChange={e => setUserForm({ ...userForm, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-outline" onClick={() => setIsAddUserModalOpen(false)}>{t('cancel')}</button>
            <button type="submit" className="btn btn-primary">{t('save')}</button>
          </div>
        </form>
      </Modal>

      {/* Password Modal */}
      <Modal isOpen={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)} title={t('changePassword')}>
        <form onSubmit={handlePasswordChange}>
          <div className="mb-4">
            <p className="text-muted mb-4">{selectedUser?.email}</p>
            <div className="input-group">
              <label>{t('newPassword')}</label>
              <input
                type="text"
                className="input-control"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-outline" onClick={() => setIsPasswordModalOpen(false)}>{t('cancel')}</button>
            <button type="submit" className="btn btn-primary">{t('save')}</button>
          </div>
        </form>
      </Modal>

      {/* Server Modal */}
      <Modal isOpen={isServerModalOpen} onClose={() => setIsServerModalOpen(false)} title={editingServer ? t('edit') : t('addServer')}>
        <form onSubmit={handleServerSubmit}>
          <div className="input-group">
            <label>{t('name')}</label>
            <input
              type="text"
              className="input-control"
              value={serverForm.name}
              onChange={e => setServerForm({ ...serverForm, name: e.target.value })}
              required
            />
          </div>

          <div className="input-group">
            <label>{t('connectionType')}</label>
            <select
              className="input-control"
              value={serverForm.connectionType}
              onChange={e => setServerForm({ ...serverForm, connectionType: e.target.value })}
            >
              <option value="ip_port">{t('ipAndPort')}</option>
              <option value="url">{t('url')}</option>
            </select>
          </div>

          {serverForm.connectionType === 'ip_port' ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="input-group">
                <label>{t('ip')}</label>
                <input
                  type="text"
                  className="input-control"
                  value={serverForm.ip}
                  onChange={e => setServerForm({ ...serverForm, ip: e.target.value })}
                  required={serverForm.connectionType === 'ip_port'}
                />
              </div>
              <div className="input-group">
                <label>{t('port')}</label>
                <input
                  type="text"
                  className="input-control"
                  value={serverForm.port}
                  onChange={e => setServerForm({ ...serverForm, port: e.target.value })}
                />
              </div>
            </div>
          ) : (
            <div className="input-group">
              <label>{t('url')}</label>
              <input
                type="url"
                className="input-control"
                value={serverForm.url}
                onChange={e => setServerForm({ ...serverForm, url: e.target.value })}
                required={serverForm.connectionType === 'url'}
              />
            </div>
          )}

          <div className="input-group">
            <label>{t('status')}</label>
            <select
              className="input-control"
              value={serverForm.status}
              onChange={e => setServerForm({ ...serverForm, status: e.target.value })}
            >
              <option value="online">Online</option>
              <option value="offline">Offline</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="btn btn-outline" onClick={() => setIsServerModalOpen(false)}>{t('cancel')}</button>
            <button type="submit" className="btn btn-primary">{t('save')}</button>
          </div>
        </form>
      </Modal>

      {/* Widget Modal */}
      <Modal isOpen={isWidgetModalOpen} onClose={() => setIsWidgetModalOpen(false)} title={editingWidget ? t('edit') : t('addWidget')}>
        <form onSubmit={handleWidgetSubmit}>
          <div className="input-group">
            <label>{t('widgetType')}</label>
            <select
              className="input-control"
              value={widgetForm.type}
              onChange={e => setWidgetForm({ ...widgetForm, type: e.target.value })}
            >
              <option value="app">{t('app')}</option>
              <option value="container">{t('container')}</option>
              <option value="youtube">{t('youtube')}</option>
              <option value="spotify">{t('spotify')}</option>
              <option value="image">{t('image')}</option>
              <option value="text">{t('text')}</option>
              <option value="clock">{t('clock')}</option>
              <option value="calendar">{t('calendar')}</option>
              <option value="stats">{t('stats')}</option>
              <option value="weather">{t('weather')}</option>
              <option value="news">{t('news')}</option>
              <option value="notifications">{t('notifications')}</option>
            </select>
          </div>
          <div className="input-group">
            <label>{t('name')}</label>
            <input
              type="text"
              className="input-control"
              value={widgetForm.title}
              onChange={e => setWidgetForm({ ...widgetForm, title: e.target.value })}
              required
            />
          </div>
          <div className="input-group">
            <label>{widgetForm.type === 'text' ? t('content') : t('url')}</label>
            <input
              type="text"
              className="input-control"
              value={widgetForm.url}
              onChange={e => setWidgetForm({ ...widgetForm, url: e.target.value })}
              required
            />
          </div>
          <div className="input-group">
            <label>{t('description')}</label>
            <input
              type="text"
              className="input-control"
              value={widgetForm.description}
              onChange={e => setWidgetForm({ ...widgetForm, description: e.target.value })}
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="btn btn-outline" onClick={() => setIsWidgetModalOpen(false)}>{t('cancel')}</button>
            <button type="submit" className="btn btn-primary">{t('save')}</button>
          </div>
        </form>
      </Modal>

      </div>
    </div>
  );
};

export default AdminDashboard;
